import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowLeft, Check, Printer, Send, Trash2, X } from 'lucide-react';
import type { Invoice } from '@mnc/contracts';
import { api } from '../../lib/api';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { Badge, ConfirmDialog, EmptyState, PageHeader, TableSkeleton, cn } from '../../components/ui';
import { AttachmentsPanel } from '../../components/records';
import { PaymentForm, ReasonDialog, toMajor } from './forms';
import { InvoiceStatusBadge, KindBadge, Money, Problems, clientName, fmtDate, useMoney, useQuantity } from './shared';

/**
 * One invoice as the client sees it: previous cumulative, this period, cumulative, the deductions and the VAT, all
 * computed on the server. A draft is editable and recomputed on every read; once issued the figures are frozen and
 * the page prints as the payment certificate.
 */
export function InvoicePage() {
  const { t, i18n } = useTranslation();
  const { id, invoiceId } = useParams({ strict: false }) as { id: string; invoiceId: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const invoice = useQuery({ queryKey: ['contracts', id, 'invoice', invoiceId], queryFn: () => api.get<Invoice>(`/api/contracts/${id}/invoices/${invoiceId}`) });
  const [editing, setEditing] = useState(false);
  const [paying, setPaying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [approving, setApproving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['contracts', id] });
  const submit = useMutation({ mutationFn: () => api.post(`/api/contracts/${id}/invoices/${invoiceId}/submit`, {}), onSuccess: () => { toast.success(t('contracts.invoiceIssued')); invalidate(); }, onError: (e) => toast.error(describeError(e, t)) });
  const approve = useMutation({ mutationFn: (approved_on: string) => api.post(`/api/contracts/${id}/invoices/${invoiceId}/approve`, { approved_on }), onSuccess: () => { invalidate(); setApproving(false); }, onError: (e) => toast.error(describeError(e, t)) });
  const cancel = useMutation({ mutationFn: (reason: string) => api.post(`/api/contracts/${id}/invoices/${invoiceId}/cancel`, { reason }), onSuccess: () => { invalidate(); setCancelling(false); }, onError: (e) => toast.error(describeError(e, t)) });
  const remove = useMutation({ mutationFn: () => api.delete(`/api/contracts/${id}/invoices/${invoiceId}`), onSuccess: () => { invalidate(); void navigate({ to: '/contracts/$id', params: { id }, search: { tab: 'invoices' } }); }, onError: (e) => toast.error(describeError(e, t)) });
  const dropPayment = useMutation({ mutationFn: (paymentId: string) => api.delete(`/api/contracts/${id}/invoices/${invoiceId}/payments/${paymentId}`), onSuccess: invalidate, onError: (e) => toast.error(describeError(e, t)) });

  const money = useMoney(invoice.data?.contract.currency);
  const qty = useQuantity();
  if (invoice.isLoading) return <TableSkeleton />;
  if (!invoice.data) return <div className="card p-10 text-center text-sm text-neutral-500">{t('errors.notFound')}</div>;
  const inv = invoice.data;
  const c = inv.contract;
  const draft = inv.status === 'draft';
  const canManage = true; // the route and the server already gate writing; the buttons follow the invoice's state
  const itemName = (itemId: string) => inv.lines.find((l) => l.boq_item_id === itemId)?.code ?? '';
  const total = (label: string, minor: number, opts: { strong?: boolean; minus?: boolean } = {}) => (
    <div className={cn('flex justify-between gap-6 py-0.5', opts.strong && 'border-t border-neutral-300 pt-1 text-base font-semibold dark:border-neutral-700')}>
      <span>{label}</span>
      <span className="tabular-nums" dir="ltr">{opts.minus && minor > 0 ? '−' : ''}{money(minor, { sign: false })}</span>
    </div>
  );

  return (
    <div>
      <nav className="no-print mb-2 text-xs text-neutral-500">
        <Link to="/contracts" search={{}} className="hover:underline">{t('contracts.pageTitle')}</Link>
        {' / '}
        <Link to="/contracts/$id" params={{ id }} search={{ tab: 'invoices' }} className="hover:underline">{c.reference}</Link>
      </nav>
      <PageHeader
        title={`${inv.number === null ? t('contracts.draftInvoice') : `${t('contracts.invoice')} #${inv.number}`} · ${t(`contracts.invoiceKinds.${inv.kind}`)}`}
        subtitle={`${pick(i18n.language, c.title_en, c.title_ar)} · ${clientName(c, i18n.language)}`}
        actions={
          <>
            <button type="button" className="btn-secondary" onClick={() => void navigate({ to: '/contracts/$id', params: { id }, search: { tab: 'invoices' } })}><ArrowLeft size={16} className="rtl:rotate-180" /> {t('app.back')}</button>
            {!draft && <button type="button" className="btn-secondary" onClick={() => window.print()}><Printer size={16} /> {t('contracts.print')}</button>}
            {draft && inv.kind === 'progress' && <button type="button" className="btn-secondary" onClick={() => setEditing(true)}>{t('contracts.editQuantities')}</button>}
            {draft && <button type="button" className="btn-secondary" onClick={() => setConfirmDelete(true)}><Trash2 size={16} /> {t('app.delete')}</button>}
            {draft && <button type="button" className="btn-primary" disabled={submit.isPending || inv.problems.length > 0} onClick={() => submit.mutate()}><Send size={16} /> {t('contracts.issueInvoice')}</button>}
            {inv.status === 'submitted' && <button type="button" className="btn-secondary" onClick={() => setCancelling(true)}><X size={16} /> {t('contracts.cancelInvoice')}</button>}
            {inv.status === 'submitted' && <button type="button" className="btn-primary" onClick={() => setApproving(true)}><Check size={16} /> {t('contracts.approveInvoice')}</button>}
            {(inv.status === 'submitted' || inv.status === 'approved') && <button type="button" className="btn-primary" onClick={() => setPaying(true)}>{t('contracts.recordPayment')}</button>}
          </>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="grid content-start gap-4">
          <div className="card grid gap-3 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <InvoiceStatusBadge status={inv.status} />
              <KindBadge kind={inv.kind} />
              {inv.frozen && <Badge tone="neutral">{t('contracts.frozen')}</Badge>}
              {inv.aging && inv.aging !== 'current' && <Badge tone="danger">{t(`contracts.buckets.${inv.aging}`)}</Badge>}
            </div>
            <div className="grid gap-3 text-sm sm:grid-cols-4">
              <div><div className="label">{t('contracts.invoiceDate')}</div><span dir="ltr">{fmtDate(inv.invoice_date, i18n.language)}</span></div>
              <div><div className="label">{t('contracts.dueDate')}</div><span dir="ltr">{fmtDate(inv.due_date, i18n.language)}</span></div>
              <div><div className="label">{t('contracts.period')}</div><span dir="ltr">{inv.period_from ? `${fmtDate(inv.period_from, i18n.language)} → ${fmtDate(inv.period_to, i18n.language)}` : '—'}</span></div>
              <div><div className="label">{t('contracts.externalRef')}</div><span dir="ltr">{inv.external_ref || '—'}</span></div>
            </div>
            {inv.cancel_reason && <p className="rounded-md bg-rose-50 p-2 text-sm text-rose-800 dark:bg-rose-950/40 dark:text-rose-200">{t('contracts.cancelled')}: {inv.cancel_reason}</p>}
            {inv.notes && <p className="whitespace-pre-wrap text-sm" dir="auto">{inv.notes}</p>}
            {draft && <Problems problems={inv.problems} quantityOf={itemName} />}
          </div>

          {inv.kind === 'progress' && (
            <div className="card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase text-neutral-500">
                    <th className="p-2 text-start">{t('contracts.itemCode')}</th>
                    <th className="p-2 text-start">{t('contracts.description')}</th>
                    <th className="p-2 text-end">{t('contracts.unitRate')}</th>
                    <th className="p-2 text-end">{t('contracts.revisedQuantity')}</th>
                    <th className="p-2 text-end">{t('contracts.previousQuantity')}</th>
                    <th className="p-2 text-end">{t('contracts.thisQuantity')}</th>
                    <th className="p-2 text-end">{t('contracts.cumulativeQuantity')}</th>
                    <th className="p-2 text-end">{t('contracts.amount')}</th>
                    <th className="p-2 text-end">{t('contracts.figures.cumulative')}</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.lines.map((l) => (
                    <tr key={l.boq_item_id} className={cn('border-t border-neutral-200 dark:border-neutral-800', l.quantity === 0 && 'text-neutral-400')}>
                      <td className="p-2" dir="ltr">{l.code}</td>
                      <td className="p-2" dir="auto">{pick(i18n.language, l.description_en, l.description_ar)}<span className="block text-xs text-neutral-500">{l.unit}</span></td>
                      <td className="p-2 text-end tabular-nums" dir="ltr">{money(l.unit_rate_minor, { sign: false })}</td>
                      <td className="p-2 text-end tabular-nums" dir="ltr">{qty(l.revised_quantity)}</td>
                      <td className="p-2 text-end tabular-nums" dir="ltr">{qty(l.previous_quantity)}</td>
                      <td className="p-2 text-end font-medium tabular-nums" dir="ltr">{qty(l.quantity)}</td>
                      <td className="p-2 text-end tabular-nums" dir="ltr">{qty(l.cumulative_quantity)}</td>
                      <td className="p-2 text-end tabular-nums" dir="ltr">{money(l.amount_minor, { sign: false })}</td>
                      <td className="p-2 text-end tabular-nums" dir="ltr">{money(l.cumulative_amount_minor, { sign: false })}</td>
                    </tr>
                  ))}
                  {!inv.lines.length && <tr><td colSpan={9} className="p-6 text-center text-sm text-neutral-500">{t('contracts.noLines')}</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {inv.payments.length > 0 && (
            <div className="card p-4">
              <h2 className="mb-2 text-sm font-semibold">{t('contracts.payments')}</h2>
              <ul className="divide-y divide-neutral-200 text-sm dark:divide-neutral-800">
                {inv.payments.map((p) => (
                  <li key={p.id} className="flex items-center justify-between gap-2 py-1">
                    <span dir="ltr">{fmtDate(p.paid_on, i18n.language)} · <Money minor={p.amount_minor} currency={c.currency} />{p.reference ? ` · ${p.reference}` : ''}</span>
                    <button type="button" className="no-print btn-secondary px-1.5 py-0.5 text-xs" onClick={() => dropPayment.mutate(p.id)} aria-label={t('app.delete')}><Trash2 size={12} /></button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="grid content-start gap-4">
          <div className="card grid gap-1 p-4 text-sm">
            <h2 className="mb-1 text-sm font-semibold">{t('contracts.invoiceTotals')}</h2>
            {inv.kind === 'progress' ? (
              <>
                {total(t('contracts.figures.previousCumulative'), inv.previous_gross_minor)}
                {total(t('contracts.figures.cumulative'), inv.cumulative_gross_minor)}
                {total(t('contracts.thisInvoice'), inv.gross_minor, { strong: true })}
                {total(t('contracts.figures.advanceRecovery'), inv.advance_recovery_minor, { minus: true })}
                {(c.retention_percent > 0 || inv.retention_minor !== 0) && total(t('contracts.figures.retention'), inv.retention_minor, { minus: true })}
              </>
            ) : (
              total(t('contracts.amount'), inv.amount_minor ?? inv.gross_minor, { strong: true })
            )}
            {inv.retention_release_minor > 0 && total(t('contracts.figures.retentionReleased'), inv.retention_release_minor)}
            {total(`${t('contracts.figures.vat')} (${new Intl.NumberFormat(i18n.language).format(c.vat_percent)}%)`, inv.vat_minor)}
            {total(t('contracts.figures.netDue'), inv.net_due_minor, { strong: true })}
            {!draft && total(t('contracts.figures.paid'), inv.paid_minor)}
            {!draft && total(t('contracts.figures.outstanding'), inv.outstanding_minor, { strong: true })}
            {inv.days_overdue !== null && inv.outstanding_minor > 0 && (
              <p className={cn('mt-1 text-xs', inv.days_overdue > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-neutral-500')}>
                {inv.days_overdue > 0 ? t('contracts.overdueBy', { days: inv.days_overdue }) : t('contracts.dueIn', { days: -inv.days_overdue })}
              </p>
            )}
            <p className="mt-2 text-xs text-neutral-500">{inv.frozen ? t('contracts.frozenHint') : t('contracts.draftHint')}</p>
          </div>
          <div className="no-print"><AttachmentsPanel ownerType="service_contract" ownerId={id} canEdit={false} compact /></div>
        </div>
      </div>

      {editing && <LinesDialog invoice={inv} contractId={id} onClose={() => setEditing(false)} />}
      <PaymentForm open={paying} onClose={() => setPaying(false)} contractId={id} invoiceId={invoiceId} currency={c.currency} outstandingMinor={inv.outstanding_minor} />
      <ReasonDialog open={approving} onClose={() => setApproving(false)} title={t('contracts.approveInvoice')} label={t('contracts.note')} dateLabel={t('contracts.approvedOn')} pending={approve.isPending} onSubmit={({ date }) => approve.mutate(date)} />
      <ReasonDialog open={cancelling} onClose={() => setCancelling(false)} title={t('contracts.cancelInvoice')} label={t('contracts.cancelReason')} requireText pending={cancel.isPending} onSubmit={({ text }) => cancel.mutate(text)} />
      <ConfirmDialog open={confirmDelete} title={t('contracts.deleteInvoice')} danger onCancel={() => setConfirmDelete(false)} onConfirm={() => { setConfirmDelete(false); remove.mutate(); }} />
      {!canManage && <EmptyState>{t('contracts.readOnly')}</EmptyState>}
    </div>
  );
}

/** What this invoice claims, item by item; "claim the rest" fills in what the contract still allows. */
function LinesDialog({ invoice, contractId, onClose }: { invoice: Invoice; contractId: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  useEffect(() => { setValues(Object.fromEntries(invoice.lines.map((l) => [l.boq_item_id, l.quantity ? String(l.quantity) : '']))); }, [invoice]);
  const save = useMutation({
    mutationFn: () => api.put(`/api/contracts/${contractId}/invoices/${invoice.id}/lines`, { lines: invoice.lines.filter((l) => Number(values[l.boq_item_id] || 0) > 0).map((l) => ({ boq_item_id: l.boq_item_id, quantity: Number(values[l.boq_item_id]) })) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['contracts', contractId] }); onClose(); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const remaining = (l: Invoice['lines'][number]) => Math.max(0, Math.round((l.revised_quantity - l.previous_quantity) * 10_000) / 10_000);
  const claimRest = () => setValues(Object.fromEntries(invoice.lines.map((l) => [l.boq_item_id, remaining(l) ? String(remaining(l)) : ''])));
  const qty = useQuantity();
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal>
      <div className="card max-h-[85vh] w-full max-w-3xl overflow-auto p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">{t('contracts.editQuantities')}</h2>
          <button type="button" className="btn-secondary px-2 py-0.5 text-xs" onClick={claimRest}>{t('contracts.claimRest')}</button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase text-neutral-500">
              <th className="p-1 text-start">{t('contracts.itemCode')}</th>
              <th className="p-1 text-start">{t('contracts.description')}</th>
              <th className="p-1 text-end">{t('contracts.previousQuantity')}</th>
              <th className="p-1 text-end">{t('contracts.remaining')}</th>
              <th className="p-1 text-end">{t('contracts.thisQuantity')}</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((l) => (
              <tr key={l.boq_item_id} className="border-t border-neutral-200 dark:border-neutral-800">
                <td className="p-1" dir="ltr">{l.code}</td>
                <td className="p-1" dir="auto">{pick(i18n.language, l.description_en, l.description_ar)}<span className="block text-xs text-neutral-500">{l.unit}</span></td>
                <td className="p-1 text-end tabular-nums" dir="ltr">{qty(l.previous_quantity)}</td>
                <td className="p-1 text-end tabular-nums" dir="ltr">{qty(remaining(l))}</td>
                <td className="p-1 text-end"><input className="input w-28 text-end" type="number" step="0.0001" min="0" dir="ltr" value={values[l.boq_item_id] ?? ''} onChange={(e) => setValues({ ...values, [l.boq_item_id]: e.target.value })} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-3 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t('app.cancel')}</button>
          <button type="button" className="btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>{t('app.save')}</button>
        </div>
      </div>
    </div>
  );
}

export { toMajor };
